import React, { Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import ShadowMount from '@/styles/ShadowMount';
import { InsideShadowReset } from '@/styles/IslandScope';
import { getIsland, preloadIsland } from './registry';
import IslandSkeleton, { type SkeletonVariant } from './IslandSkeleton';
import Loader from '@/components/atoms/UI/Loader/Loader';

/**
 * Фоллбек для крупных островов (Testing, TestResult, Profile, Contacts) —
 * обычный спиннер, тот же Loader, что эти компоненты показывают при загрузке
 * данных. Скелетон им не нужен: свой лоадер у них уже есть, и вдвоём они
 * давали двойную индикацию загрузки.
 */
const SpinnerWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 6rem 0;
`;

interface IslandMount {
    id: string;
    name: string;
    props: Record<string, unknown>;
    element: Element;
}

/**
 * Считывает значение `data-test-action` с ближайшего предка-триггера.
 * Используется для deferred-островов, где blade-кнопки несут атрибут
 * `data-test-action="start|continue|clearAndStart|lastAttempt|clear"`,
 * и Island должен знать, какая кнопка инициировала монтирование.
 */
/**
 * Силуэт заглушки, который blade уже отрендерил в light DOM
 * (<x-react-island skeleton-variant="cards" …>). Читаем его с host'а, чтобы
 * Suspense-фоллбек внутри shadow выглядел ровно так же — иначе на переходе
 * «ванильная заглушка → React-заглушка» менялась бы картинка.
 *
 * null — у host'а нет data-skeleton (<x-react-island :skeleton="false"> или
 * deferred-контейнер): остров крупный, фоллбек — спиннер, не скелетон.
 */
function getSkeletonFromHost(host: Element): { variant: SkeletonVariant; parts: number } | null {
    const variant = host.getAttribute('data-skeleton');
    if (variant === null) return null;
    const parts = Number(host.getAttribute('data-skeleton-parts'));
    return {
        variant: variant === 'cards' || variant === 'form' ? variant : 'block',
        parts: Number.isFinite(parts) && parts > 0 ? parts : 1,
    };
}

function getActionFromTarget(target: Element, container: Element): string | null {
    const trigger = target.closest('[data-test-action]');
    if (!trigger || !container.contains(trigger)) return null;
    return trigger.getAttribute('data-test-action');
}

/**
 * Каждый остров оборачивается в собственный `ShadowMount` — `attachShadow`
 * вешается на сам легаси-узел `[data-react-island]`, и React-контент
 * рендерится **внутри** shadow root через портал. Так Bootstrap 3 и
 * прочая легаси-нормализация физически не достают до компонентов
 * (и наоборот — наши стили не текут на легаси-страницу).
 *
 * `InsideShadowReset` рендерится первым — это `createGlobalStyle`,
 * который инжектится в shadow root и даёт element-level reset'ы
 * (margin/padding на типографии, дефолты input/button/form и т.п.).
 *
 * ThemeProvider / Redux Provider / I18n / Router — выше в дереве
 * (`AppIslands.tsx`), их контекст пропускается через `createPortal`
 * штатно в React 18+.
 */
export const IslandsContainer: React.FC = () => {
    const [mounts, setMounts] = useState<IslandMount[]>([]);

    useEffect(() => {
        // ── Inline острова: монтируются сразу при загрузке ──────────────────
        const inlineElements = document.querySelectorAll('[data-react-island]');
        const initial: IslandMount[] = [];

        inlineElements.forEach((el, i) => {
            const name = el.getAttribute('data-react-island') ?? '';
            const propsRaw = el.getAttribute('data-props') ?? '{}';
            let props: Record<string, unknown> = {};
            try { props = JSON.parse(propsRaw); } catch { /* malformed props — skip */ }
            initial.push({ id: `island-${i}`, name, props, element: el });
        });

        setMounts(initial);

        // ── Deferred острова: монтируются ПО КЛИКУ внутри контейнера ────────
        // Контейнер несёт data-react-island-deferred="Name" и
        // data-mount-on="<селектор-триггеров>". При клике на любой матчинговый
        // потомок — мы парсим data-props, добавляем clickedBtn из data-test-action
        // (если есть на триггере) и монтируем компонент, заменяя исходный HTML.
        // Используется на странице теста: blade рендерит стартовый экран для
        // SEO, React не грузится в DOM до клика «Пройти».
        const deferredElements = document.querySelectorAll('[data-react-island-deferred]');
        const cleanups: Array<() => void> = [];

        deferredElements.forEach((el, i) => {
            const name = el.getAttribute('data-react-island-deferred') ?? '';
            const triggers = el.getAttribute('data-mount-on') ?? '';
            const propsRaw = el.getAttribute('data-props') ?? '{}';
            if (!name || !triggers) return;

            const handler = (e: Event) => {
                const target = e.target;
                if (!(target instanceof Element)) return;
                const matched = target.closest(triggers);
                if (!matched || !el.contains(matched)) return;

                e.preventDefault();
                e.stopPropagation();

                let props: Record<string, unknown> = {};
                try { props = JSON.parse(propsRaw); } catch { /* malformed props */ }

                const action = getActionFromTarget(target, el);
                if (action) {
                    props = { ...props, clickedBtn: action };
                }

                setMounts(prev => {
                    if (prev.some(m => m.element === el)) return prev;
                    // Очищаем серверный HTML (стартовый экран теста) — React возьмёт
                    // полный контроль над контейнером через portal. createPortal сам
                    // не удаляет существующих children, поэтому делаем это вручную.
                    el.replaceChildren();
                    return [
                        ...prev,
                        { id: `deferred-${name}-${i}`, name, props, element: el },
                    ];
                });
            };

            el.addEventListener('click', handler);
            cleanups.push(() => el.removeEventListener('click', handler));

            // Прогреваем чанк отложенного острова в простое браузера, чтобы клик
            // по триггеру (напр. «Пройти тест») смонтировал компонент мгновенно,
            // не дожидаясь сетевой загрузки ленивого чанка (иначе вырос бы INP
            // именно этого ключевого взаимодействия).
            const warm = () => preloadIsland(name);
            const ric = (window as unknown as {
                requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
            }).requestIdleCallback;
            if (typeof ric === 'function') {
                ric(warm, { timeout: 3000 });
            } else {
                setTimeout(warm, 1500);
            }
        });

        return () => cleanups.forEach(fn => fn());
    }, []);

    return (
        <>
            {mounts.map((mount) => {
                const Component = getIsland(mount.name);
                if (!Component) return null;
                const skeleton = getSkeletonFromHost(mount.element);
                return (
                    <ShadowMount host={mount.element} key={mount.id}>
                        <InsideShadowReset />
                        {/*
                          * Фоллбек, а не null: у shadow-острова ванильная
                          * заглушка (light-ребёнок host'а) перестаёт
                          * рендериться сразу при attachShadow — то есть до
                          * загрузки ленивого чанка. Без фоллбека здесь между
                          * ними зияла бы пустота, и контент появлялся рывком.
                          */}
                        <Suspense
                            fallback={
                                skeleton ? (
                                    <IslandSkeleton
                                        variant={skeleton.variant}
                                        parts={skeleton.parts}
                                    />
                                ) : (
                                    <SpinnerWrap><Loader /></SpinnerWrap>
                                )
                            }
                        >
                            <Component {...mount.props} />
                        </Suspense>
                    </ShadowMount>
                );
            })}
        </>
    );
};
